import {
  CollectionPaths,
  readLoyaltyEarnRule,
  settingsMapFieldWrites,
  withoutDedicatedSettingsKeys,
} from '@order-up/common';
import {
  CARD_PAYMENT_LOCKED_FIELDS,
  ILocation,
  Location as LocationRecord,
  type LoyaltyEarnRule,
  type OnlineServiceFeeLine,
  Tenant,
} from '@order-up/models';
import {
  CreateLocationSchema,
  CreateLocationType,
  LocationSchema,
} from '@order-up/schemas';
import { FieldValue, deleteField } from 'firebase/firestore';
import { DocumentWithId, FirestoreRepository } from '../interfaces/FirestoreRepository';
import { removeUndefinedValues } from '../utils/firestore';
import { createLogger } from '../utils/logger';
import { readOnlineServiceFeeLine } from './onlineServiceFeeLine';
import { createApiClient, getApi } from './apiClient';
import { counterService } from './counters';
import { resolveLocationDocId, resolveTenantDocId } from './resolveDocIds';
import { tenantService } from './tenants';

const logger = createLogger('RestaurantService');

const locationsApi = createApiClient('/api/v1/locations');

/**
 * Card-payment fields are written ONLY by Site Admin -> Card payments (the
 * staff-only server route). Any locked key (or a dotted sub-key of one, e.g.
 * `trxCredentials.clientId`) that reaches an owner/browser writer is dropped
 * here, loudly, so a stale caller can never write them straight to Firestore.
 */
function dropLockedCardPaymentFields<T extends Record<string, unknown>>(input: T, where: string): T {
  const locked = CARD_PAYMENT_LOCKED_FIELDS as readonly string[];
  const dropped = Object.keys(input).filter((k) => locked.includes(k.split('.')[0] ?? k));
  if (dropped.length === 0) return input;
  logger.warn(`${where}: dropped card-payment fields — edit them on Site Admin → Card payments`, { dropped });
  const copy: Record<string, unknown> = { ...input };
  for (const k of dropped) delete copy[k];
  return copy as T;
}

/**
 * Recursively converts Date objects to Firestore server timestamps and removes undefined values
 * This ensures that nested Date objects (like lastSync in platformSync) are properly stored as Firestore timestamps
 * and that undefined values are filtered out (Firestore doesn't support undefined)
 */
const convertDatesToTimestamps = (obj: any, path: string[] = []): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  // Only convert specific timestamp fields to serverTimestamp, not all dates
  const currentKey = path.length > 0 ? path[path.length - 1] : '';
  const isTimestampField = ['connectedAt', 'lastSyncAt', 'createdAt', 'updatedAt'].includes(currentKey);

  if (obj instanceof Date) {
    return isTimestampField ? FirestoreRepository.getServerTimestamp() : obj;
  }

  // Handle ISO date strings - only convert timestamp fields to serverTimestamp
  if (typeof obj === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(obj)) {
    if (isTimestampField) {
      logger.debug(`Converting timestamp field "${currentKey}" at path [${path.join(' -> ')}] to serverTimestamp`);
      return FirestoreRepository.getServerTimestamp();
    }
    return obj;
  }

  if (Array.isArray(obj)) {
    // For arrays, never use serverTimestamp - keep original values
    logger.debug(`🔧 Processing array at path [${path.join(' -> ')}] - preserving original date values`);
    return obj.map((item, index) => convertDatesToTimestamps(item, [...path, index.toString()])).filter(item => item !== undefined);
  }

  if (typeof obj === 'object') {
    const converted: any = {};
    for (const [key, value] of Object.entries(obj)) {
      const convertedValue = convertDatesToTimestamps(value, [...path, key]);
      // Only include the field if the value is not undefined
      if (convertedValue !== undefined) {
        converted[key] = convertedValue;
      }
    }
    return converted;
  }

  return obj;
};

/**
 * Recursively converts Firestore Timestamps to JavaScript Date objects for schema validation
 * This ensures that Firestore timestamps are properly converted back to Date objects before validation
 */
const convertTimestampsToDates = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  // Handle Firestore Timestamp objects
  if (obj && typeof obj.toDate === 'function') {
    return obj.toDate();
  }

  // Handle objects with seconds (Firestore timestamp format)
  if (obj && typeof obj === 'object' && obj.seconds && obj.nanoseconds !== undefined) {
    return new Date(obj.seconds * 1000 + obj.nanoseconds / 1000000);
  }

  if (Array.isArray(obj)) {
    return obj.map(convertTimestampsToDates);
  }

  if (typeof obj === 'object') {
    const converted: any = {};
    for (const [key, value] of Object.entries(obj)) {
      converted[key] = convertTimestampsToDates(value);
    }
    return converted;
  }

  return obj;
};

/**
 * Recursively converts Date objects to ISO strings for Zod schema validation
 * This ensures Date objects are properly formatted as strings for validation
 */
const convertDatesForValidation = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (obj instanceof Date) {
    return obj.toISOString();
  }

  if (Array.isArray(obj)) {
    return obj.map(convertDatesForValidation);
  }

  if (typeof obj === 'object') {
    const converted: any = {};
    for (const [key, value] of Object.entries(obj)) {
      converted[key] = convertDatesForValidation(value);
    }
    return converted;
  }

  return obj;
};

/**
 * Generates a URL-safe slug from a restaurant name
 * Matches the implementation in core service for consistency
 */
const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')      // Replace all non-alphanumeric chars with hyphens
    .replace(/(^-|-$)+/g, '');        // Remove leading/trailing hyphens
};

/**
 * Gets all restaurants for a tenant by tenant business ID
 * @param tenantId The business tenant ID (number)
 * @returns Array of restaurants
 */
const getLocationsByTenantId = async (tenantId: number): Promise<DocumentWithId<LocationRecord>[]> => {
  logger.debug(`Getting restaurants for tenantId: ${tenantId}`);

  const tenantDocId = await resolveTenantDocId(tenantId);
  if (!tenantDocId) {
    logger.warn(`Tenant not found with tenantId: ${tenantId}`);
    return [];
  }

  try {
    const locationCollectionPath = CollectionPaths.restaurants(tenantDocId);
    const locationRepo = new FirestoreRepository<LocationRecord>(locationCollectionPath, LocationRecord);
    const locationsWithDocIds = await locationRepo.find(); // find() with no conditions gets all

    // Timestamp conversion now handled in Restaurant model constructor
    return locationsWithDocIds;
  } catch (error) {
    // Rethrow — do NOT return []. A swallowed error here is indistinguishable
    // from "this tenant has no restaurants", which made the restaurant-selector's
    // error branch dead code and told the user "No restaurants found" during an
    // outage, blocking sign-in with no retry. TanStack surfaces the throw as
    // isError so callers can retry. A genuinely empty tenant returns [] above.
    logger.error(`Failed to get restaurants for tenantId ${tenantId}`, { error: error instanceof Error ? error.message : String(error), tenantId });
    throw error;
  }
};

/**
 * Gets a single restaurant by business restaurant ID and tenant ID
 * @param locationNumericId The business restaurant ID (number)
 * @param tenantId The business tenant ID (number)
 * @returns Restaurant or null if not found
 */
const getLocationById = async (locationNumericId: number, tenantId?: number): Promise<DocumentWithId<LocationRecord> | null> => {
  logger.debug(`Getting restaurant by ID: ${locationNumericId}, tenantId: ${tenantId}`);

  // If tenantId is provided, use it for more efficient lookup
  if (tenantId) {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      logger.warn(`Tenant not found with tenantId: ${tenantId}`);
      return null;
    }

    try {
      const locationCollectionPath = CollectionPaths.restaurants(tenantDocId);
      const locationRepo = new FirestoreRepository<LocationRecord>(locationCollectionPath, LocationRecord);
      const results = await locationRepo.find([{ field: 'restaurantId', operator: '==', value: locationNumericId }]);

      if (results.length === 0) return null;
      return results[0];
    } catch (error) {
      // Rethrow: null must mean "not found", never "the read failed". See the
      // note in getRestaurantsByTenantId — swallowing this hid outages behind a
      // false "restaurant not found".
      logger.error(`Failed to get restaurant ${locationNumericId}`, { error: error instanceof Error ? error.message : String(error), restaurantId: locationNumericId });
      throw error;
    }
  }

  // If no tenantId provided, we'd need to search across all tenants (not recommended for performance)
  logger.warn(`No tenantId provided for restaurant lookup - this is inefficient`);
  return null;
};

/**
 * Gets a single restaurant by GUID
 * @param guid The restaurant GUID
 * @param tenantId Optional tenant ID for more efficient lookup
 * @returns Restaurant or null if not found
 */
const getLocationByGuid = async (guid: string, tenantId?: number): Promise<DocumentWithId<LocationRecord> | null> => {
  logger.debug(`Getting restaurant by GUID: ${guid}, tenantId: ${tenantId}`);

  if (tenantId) {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      logger.warn(`Tenant not found with tenantId: ${tenantId}`);
      return null;
    }

    try {
      const locationCollectionPath = CollectionPaths.restaurants(tenantDocId);
      const locationRepo = new FirestoreRepository<LocationRecord>(locationCollectionPath, LocationRecord);
      const results = await locationRepo.find([{ field: 'guid', operator: '==', value: guid }]);

      if (results.length === 0) return null;
      return results[0];
    } catch (error) {
      // Rethrow: null must mean "not found", never "the read failed".
      logger.error(`Failed to get restaurant by GUID ${guid}`, { error: error instanceof Error ? error.message : String(error), guid });
      throw error;
    }
  }

  // If no tenantId provided, search would be across all tenants (not recommended)
  logger.warn(`No tenantId provided for restaurant GUID lookup - this is inefficient`);
  return null;
};

/** Envelope of `GET /api/v1/locations/slug/:slug` (public, sanitized). */
interface LocationBySlugEnvelope {
  success: boolean;
  data?: {
    restaurant?: Record<string, unknown> & {
      docId?: string;
      /**
       * Name-only projection of the payment routing added by the route for
       * the checkout UI (see the route comment in
       * apps/api/components/routes/locations.ts). The full `providerRouting` // scoped-path-ok: module path
       * policy map never leaves the server on public endpoints.
       */
      paymentProviderPublic?: { defaultProvider?: string };
    };
    tenant?: (Record<string, unknown> & { docId?: string }) | null;
    /**
     * The tenant's `initiative.scheduled_menu_pricing`, evaluated server-side
     * (a storefront guest has no session to evaluate it with). Fail-closed.
     */
    scheduledMenuPricing?: boolean;
    /**
     * Whether this location's guest loyalty UI is on: `initiative.customer_loyalty`
     * AND an `active` program, evaluated server-side. Fail-closed.
     */
    customerLoyalty?: boolean;
    /**
     * The active program's earn rule, only while `customerLoyalty` is on
     * (null otherwise). Untrusted until `readLoyaltyEarnRule` parses it.
     */
    customerLoyaltyEarnRule?: unknown;
    /** The server's clock when it answered (ISO). */
    serverNow?: string;
    /**
     * The Online Service Fee the cart shows, resolved by the API with the
     * tenant flag evaluated. null = no line.
     */
    onlineServiceFee?: { amount: number; tax: number; label: string } | null;
    /** The location's own serving website domain (hostname), when it has one. */
    primaryDomain?: string;
    /**
     * The location's ACTIVE tax rates, read by the API (the same rates
     * /orders/preview charges with). null = the server could not read them.
     */
    taxRates?: unknown;
  };
}

/** What a slug resolves to on the storefront. */
export interface LocationBySlug {
  restaurant: DocumentWithId<LocationRecord>;
  tenant: DocumentWithId<Tenant>;
  /**
   * Scheduled menu pricing (Bar v2 Part D) is on for this tenant: the
   * storefront prices an item through an open Happy Hour. Anything but `true`
   * — absent from an older payload or seed included — is OFF.
   */
  scheduledMenuPricing?: boolean;
  /**
   * Customer loyalty is LIVE for this location — the storefront's "Sign in",
   * the cart's "Earn N points", "Pay With Points" and checkout's redeem/enroll.
   * The server's answer (`initiative.customer_loyalty` AND an `active`
   * program). Anything but `true` — absent from an older payload or seed
   * included — is OFF, so no loyalty UI shows while the program is dark.
   */
  customerLoyalty?: boolean;
  /**
   * The active program's earn rule — the cart's "Earn N points" is computed
   * from it with the same `computeEarnPoints` the accrual awards with. null /
   * absent (loyalty off, a malformed rule, an older payload) → the cart shows
   * no points line rather than a guessed number.
   */
  customerLoyaltyEarnRule?: LoyaltyEarnRule | null;
  /**
   * Server clock minus this browser's clock, measured when the response
   * arrived (`serverNow`). The storefront's checkout-start Happy Hour check
   * runs on `Date.now() + serverClockOffsetMs`, so it agrees with the server's
   * cart re-pricing however wrong the guest's clock is. Absent on a seed (a
   * cached page's `serverNow` is as old as the page) and on older payloads.
   */
  serverClockOffsetMs?: number;
  /**
   * The server-resolved Online Service Fee line (null = none). The cart never
   * SHOWS this value — it can be minutes old (SSR seed) — it only decides
   * whether the cart re-fetches it fresh (`useLocationOnlineServiceFee`).
   */
  onlineServiceFee?: OnlineServiceFeeLine | null;
  /**
   * The location's own website domain (hostname only), resolved by the API
   * from its verified hosting domains. Checkout trusts it as a place to send
   * the guest back to ("Back to menu", "Order again").
   */
  primaryDomain?: string;
  /**
   * The location's active tax rates as the SERVER read them — what the cart
   * estimates tax with. `null` (or absent, on an older payload or seed) means
   * NOT KNOWN: the cart shows tax as calculated at checkout, never $0. `[]`
   * means the location charges no tax. RAW as the server sent it — read it
   * only through `readPublicTaxRates` (`useLocationGuestTaxRates` does).
   */
  taxRates?: unknown;
}

/**
 * Gets a single restaurant (+ its tenant) by slug via the PUBLIC API endpoint
 * `GET /api/v1/locations/slug/:slug` (Admin-SDK lookup, sanitized through
 * `sanitizeRestaurantForPublic` / `sanitizeTenantForPublic` server-side).
 *
 * PR-0: this used to run a CLIENT-SDK restaurants collection-group query,
 * which required the Firestore collection-group rule to stay readable by any
 * authenticated visitor and handed the RAW restaurant doc to the browser.
 * The rules read is now site-admin-only; public slug resolution goes through
 * the API's sanitized projection instead (same endpoint the restaurant-site
 * SSR already uses). Return shape is unchanged: `{ restaurant, tenant }`
 * model instances with `docId` stamped, or `null` when not found / on error.
 *
 * The checkout's `restaurant.providerRouting?.defaultProvider` read keeps
 * working: the route ships the provider NAME as `paymentProviderPublic` and
 * it is rebuilt into a `providerRouting = { defaultProvider }` stub here —
 * the rest of the routing policy map deliberately never reaches the client.
 *
 * @param slug The restaurant slug (e.g., "pizza-palace-downtown")
 * @returns Restaurant with tenant data or null if not found
 */
/** `{ serverClockOffsetMs }` from a just-received `serverNow`, or nothing when it is absent or unparseable. */
function serverClockOffset(serverNow: string | undefined): { serverClockOffsetMs?: number } {
  const at = serverNow ? Date.parse(serverNow) : Number.NaN;
  return Number.isFinite(at) ? { serverClockOffsetMs: at - Date.now() } : {};
}

const getLocationBySlug = async (slug: string): Promise<LocationBySlug | null> => {
  logger.debug(`Getting restaurant by slug via public API: ${slug}`);

  if (!slug || typeof slug !== 'string') {
    logger.warn(`Invalid slug provided: ${slug}`);
    return null;
  }

  try {
    const response = await getApi<LocationBySlugEnvelope>(
      locationsApi,
      `/slug/${encodeURIComponent(slug)}`,
    );

    const rawLocation = response?.data?.restaurant;
    const rawTenant = response?.data?.tenant;
    if (!response?.success || !rawLocation || !rawTenant) {
      logger.debug(`No restaurant found with slug: ${slug}`);
      return null;
    }

    // Rebuild the checkout's provider selector from the name-only projection.
    // `Restaurant.fromJSON` maps `data.providerRouting` — feeding the stub in
    // through it keeps `restaurant.providerRouting?.defaultProvider` working
    // for every existing consumer with no field-access changes.
    const { paymentProviderPublic, ...locationJson } = rawLocation;
    const defaultProvider = paymentProviderPublic?.defaultProvider;
    const locationRecord = LocationRecord.fromJSON({
      ...locationJson,
      ...(defaultProvider ? { providerRouting: { defaultProvider } } : {}),
    });
    (locationRecord as DocumentWithId<LocationRecord>).docId = rawLocation.docId ?? '';

    const tenant = Tenant.fromJSON(rawTenant);
    (tenant as DocumentWithId<Tenant>).docId = rawTenant.docId ?? '';

    logger.debug(`✅ Found restaurant "${locationRecord.name}" in tenant "${tenant.name}" via public API`);

    return {
      restaurant: locationRecord as DocumentWithId<LocationRecord>,
      tenant: tenant as DocumentWithId<Tenant>,
      scheduledMenuPricing: response.data?.scheduledMenuPricing === true,
      customerLoyalty: response.data?.customerLoyalty === true,
      customerLoyaltyEarnRule: readLoyaltyEarnRule(response.data?.customerLoyaltyEarnRule),
      onlineServiceFee: readOnlineServiceFeeLine(response.data?.onlineServiceFee),
      ...(typeof response.data?.primaryDomain === 'string' && response.data.primaryDomain
        ? { primaryDomain: response.data.primaryDomain }
        : {}),
      // RAW: parsed by `readPublicTaxRates` where the cart uses it, so the
      // validator stays out of checkout's budgeted initial download.
      taxRates: response.data?.taxRates,
      ...serverClockOffset(response.data?.serverNow),
    };

  } catch (error) {
    // 404 = genuinely unknown slug — same `null` contract as the old
    // empty-snapshot branch; anything else is logged as an error. Both return
    // null, matching the previous error behavior of this function.
    const status = (error as { status?: number }).status;
    if (status === 404) {
      logger.debug(`No restaurant found with slug: ${slug}`);
    } else {
      logger.error(`Failed to get restaurant by slug ${slug}`, { error: error instanceof Error ? error.message : String(error), slug });
    }
    return null;
  }
};

/**
 * Creates a new restaurant document in Firestore after validating the input data.
 * Uses counter service for ID generation when restaurantId and guid are not provided.
 * For mock data with predefined IDs, those values are used directly.
 *
 * Card-payment fields (`CARD_PAYMENT_LOCKED_FIELDS`) are NOT written here —
 * they are dropped with a warning; Site Admin -> Card payments owns them.
 *
 * @param locationData The raw data for the new restaurant conforming to CreateRestaurantType.
 */
const createLocation = async (
  rawLocationData: CreateLocationType,
): Promise<DocumentWithId<LocationRecord>> => {
  const locationData = dropLockedCardPaymentFields(
    rawLocationData as CreateLocationType & Record<string, unknown>,
    'createLocation',
  ) as CreateLocationType;
  logger.debug(`Creating restaurant for tenantId: ${locationData.tenantId}`);

  const tenantDocId = await resolveTenantDocId(locationData.tenantId);
  if (!tenantDocId) {
    // Reads as "tenant doc not present in Firestore." Common causes:
    // an earlier tenant-create failed after the counter advanced
    // (leaving a phantom ID), the tenant was deleted, or the admin
    // selected from a stale list. Wording nudges the user toward a
    // refresh rather than re-typing the ID.
    throw new Error(
      `Tenant ${locationData.tenantId} could not be resolved — it may have been deleted or never finished provisioning. Refresh the tenant list and try again.`,
    );
  }

  // Validate data using the creation schema
  const validatedData = CreateLocationSchema.parse(locationData);
  logger.debug(`Restaurant data passed validation`);

  // Always generate IDs using counter service to ensure proper testing
  logger.debug(`Generating new restaurantId and guid using counter service`);
  const generated = await counterService.generateRestaurantId();
  const locationNumericId = generated.restaurantId;
  const guid = generated.guid;

  // Create restaurant payload with server timestamps and generated/provided IDs
  // Exclude optional fields from spread and add them conditionally.
  const { platformSync, websiteConfig, ...baseValidatedData } = validatedData;

  const basePayload: any = {
    ...baseValidatedData,
    tenantId: validatedData.tenantId,
    restaurantId: locationNumericId,
    guid,
    slug: generateSlug(validatedData.name),
    createdAt: FirestoreRepository.getServerTimestamp(),
    updatedAt: FirestoreRepository.getServerTimestamp(),
  };

  // Only include optional fields if they exist (Firestore doesn't support undefined)
  if (platformSync) {
    basePayload.platformSync = convertDatesToTimestamps(platformSync, ['platformSync']);
  }

  if (websiteConfig) {
    basePayload.websiteConfig = convertDatesToTimestamps(websiteConfig, ['websiteConfig']);
  }

  // Remove undefined values before sending to Firestore (matches pattern in all other services)
  const locationPayload = removeUndefinedValues(basePayload);

  try {
    const locationCollectionPath = CollectionPaths.restaurants(tenantDocId);
    const locationRepo = new FirestoreRepository<LocationRecord>(locationCollectionPath, LocationRecord);
    const created = await locationRepo.create(locationPayload);
    logger.info('Restaurant created', {
      restaurantId: locationNumericId,
      tenantDocId,
    });

    return created;
  } catch (error) {
    logger.error(`Failed to create restaurant`, { error: error instanceof Error ? error.message : String(error) });
    throw error;
  }
};

/**
 * Updates an existing restaurant by GUID
 * @param locationGuid The restaurant GUID
 * @param tenantGuid The tenant GUID
 * @param locationData Partial restaurant data to update
 */
const updateLocationByGuid = async (locationGuid: string, tenantGuid: string, rawLocationData: Partial<ILocation>): Promise<void> => {
  const locationData = dropLockedCardPaymentFields(
    rawLocationData as Record<string, unknown>,
    'updateLocationByGuid',
  ) as Partial<ILocation>;
  logger.debug(`Updating restaurant by GUID: ${locationGuid}, tenant GUID: ${tenantGuid}`);

  // Get tenant first to get tenantId
  const tenant = await tenantService.getTenantByGuid(tenantGuid);
  if (!tenant) {
    throw new Error(`Tenant not found with GUID: ${tenantGuid}`);
  }

  // Get restaurant to get restaurantId
  const locationRecord = await getLocationByGuid(locationGuid, tenant.tenantId);
  if (!locationRecord) {
    throw new Error(`Restaurant not found with GUID: ${locationGuid}`);
  }

  // Use existing updateRestaurant function with numeric IDs
  await updateLocation(locationRecord.restaurantId, locationRecord.tenantId, locationData);
  logger.debug(`Successfully updated restaurant by GUID: ${locationGuid}`);
};

/**
 * Updates an existing restaurant
 * @param locationNumericId The business restaurant ID (number)
 * @param tenantId The business tenant ID (number)
 * @param locationData Partial restaurant data to update
 */
const updateLocation = async (
  locationNumericId: number,
  tenantId: number,
  rawLocationData: Partial<ILocation> | Record<string, any>,
): Promise<void> => {
  logger.debug(`Updating restaurant ${locationNumericId} for tenantId: ${tenantId}`);

  const patch = dropLockedCardPaymentFields(rawLocationData as Record<string, unknown>, 'updateLocation');

  const tenantDocId = await resolveTenantDocId(tenantId);
  if (!tenantDocId) {
    throw new Error(`Tenant not found with tenantId: ${tenantId}`);
  }

  const residualData: Record<string, any> = { ...patch };

  // Separate dot-notation keys (e.g. 'settings.alerts') from structured fields.
  // Dot-notation keys are atomic sub-field writes that bypass Zod validation
  // and go directly to Firestore update() — preventing clobber of sibling fields.
  //
  // A Firestore FieldValue sentinel (deleteField()/serverTimestamp()/…) is
  // ALSO an atomic op, so it is routed to the same bypass bucket even when its
  // key has no dot — otherwise a top-level delete like
  // `{ websiteConfigDraft: deleteField() }` (the "Discard draft" / "Publish
  // changes" clear) would be handed to `RestaurantSchema.partial().parse` and
  // throw, since a sentinel is not a valid field value.
  const dotNotationFields: Record<string, any> = {};
  const structuredFields: Record<string, any> = {};
  for (const [key, value] of Object.entries(residualData)) {
    if (key.includes('.') || value instanceof FieldValue) {
      dotNotationFields[key] = value;
    } else {
      structuredFields[key] = value;
    }
  }

  // Build update payload starting with dot-notation fields (already validated by caller)
  const baseUpdatePayload: any = {
    ...removeUndefinedValues(dotNotationFields),
    updatedAt: FirestoreRepository.getServerTimestamp(),
  };

  // ⚠️ A `settings` MAP IS WRITTEN KEY BY KEY, NEVER AS A WHOLE.
  // A structured `settings` map used to REPLACE the stored map, so any writer
  // that round-trips it (the site-admin RestaurantModal) deleted every key it
  // did not carry — the acceptance mode, and now `settings.printing`, which the
  // modal has no control for. So the map is expanded to one `settings.<key>`
  // write per key it NAMES: a named key is set (or deleted, when the writer
  // named it with no value — how the modal clears a time field), and a key the
  // writer does not name is left exactly as stored. Keys with a dedicated
  // editor (RESTAURANT_SETTINGS_KEYS_WITH_DEDICATED_WRITER) are dropped from
  // the map entirely: a map hydrated before another screen's save must neither
  // revert nor delete them.
  let namedSettings: Record<string, unknown> | null = null;
  const rawSettings = structuredFields.settings;
  if (rawSettings !== null && typeof rawSettings === 'object' && !Array.isArray(rawSettings)) {
    namedSettings = withoutDedicatedSettingsKeys(rawSettings as Record<string, unknown>);
    if (Object.keys(namedSettings).length > 0) structuredFields.settings = namedSettings;
    else delete structuredFields.settings;
  }

  // Process structured fields through Zod validation (existing flow)
  if (Object.keys(structuredFields).length > 0) {
    // Filter out database-specific fields before validation
    const { docId, createdAt, updatedAt, ...dataToValidate } = structuredFields as any;

    // Convert Firestore Timestamps to Date objects, then Date objects to ISO strings for validation
    const dataWithDates = convertTimestampsToDates(dataToValidate);
    const dataForValidation = convertDatesForValidation(dataWithDates);

    // Validate data
    const validatedData = LocationSchema.partial().parse(dataForValidation);

    // Exclude optional fields from spread and add them conditionally
    const { platformSync, websiteConfig, ...baseValidatedData } = validatedData;

    // Convert any nested Date objects to Firestore timestamps for base data
    const processedBaseData = convertDatesToTimestamps(baseValidatedData, []);

    if (namedSettings) {
      const validatedSettings = (processedBaseData as Record<string, unknown>).settings as
        | Record<string, unknown>
        | undefined;
      delete (processedBaseData as Record<string, unknown>).settings;
      Object.assign(
        processedBaseData,
        settingsMapFieldWrites(namedSettings, validatedSettings, deleteField()),
      );
    }

    // Merge validated structured fields into the payload
    Object.assign(baseUpdatePayload, processedBaseData);

    // Only include optional fields if they exist (Firestore doesn't support undefined)
    if (platformSync) {
      baseUpdatePayload.platformSync = convertDatesToTimestamps(platformSync, ['platformSync']);
    }

    if (websiteConfig) {
      baseUpdatePayload.websiteConfig = convertDatesToTimestamps(websiteConfig, ['websiteConfig']);
    }
  }

  // Remove undefined values before sending to Firestore (matches pattern in all other services)
  const updatePayload = removeUndefinedValues(baseUpdatePayload);

  try {
    const locationCollectionPath = CollectionPaths.restaurants(tenantDocId);
    const locationRepo = new FirestoreRepository<LocationRecord>(locationCollectionPath, LocationRecord);

    await locationRepo.updateByQuery(
      [{ field: 'restaurantId', operator: '==', value: locationNumericId }],
      updatePayload,
    );

    logger.debug(`Successfully updated restaurant ${locationNumericId}`, {
    });
  } catch (error) {
    logger.error(`Failed to update restaurant ${locationNumericId}`, { error: error instanceof Error ? error.message : String(error), restaurantId: locationNumericId });
    throw error;
  }
};

/**
 * Syncs local data to Firestore subcollections for a restaurant
 * @param tenantDocId The tenant's Firestore document ID
 * @param locationDocId The restaurant's Firestore document ID
 * @param data The data to sync
 */
const syncToFirestore = async (tenantDocId: string, locationDocId: string, data: Record<string, unknown>): Promise<void> => {
  logger.debug(`Syncing data to restaurant subcollections`);

  // Use the repository's batch subcollection method
  const locationCollectionPath = CollectionPaths.restaurants(tenantDocId);
  await FirestoreRepository.batchCreateInSubcollections(
    locationCollectionPath,
    locationDocId,
    data as Record<string, any[]>
  );

  logger.debug(`Successfully synced data`);
};

/**
 * Fetches all subcollection data for a restaurant
 * @param tenantDocId The tenant's Firestore document ID
 * @param locationDocId The restaurant's Firestore document ID
 * @returns Object with subcollection data
 */
const fetchFromFirestore = async (tenantDocId: string, locationDocId: string): Promise<Record<string, any[]>> => {
  logger.debug(`Fetching restaurant subcollection data`);

  const data: Record<string, any[]> = {};
  const subcollectionNames = ['menu_groups', 'items', 'courses', 'employees', 'tags'];

  try {
    // Use a simple repository instance for subcollection operations
    const locationCollectionPath = CollectionPaths.restaurants(tenantDocId);
    const repo = new FirestoreRepository<LocationRecord>(locationCollectionPath, LocationRecord);

    for (const subcollectionName of subcollectionNames) {
      const subcollectionData = await repo.getSubcollectionDocuments(locationDocId, subcollectionName);
      data[subcollectionName] = subcollectionData;
    }
  } catch (error) {
    logger.error(`Failed to fetch subcollection data`, { error: error instanceof Error ? error.message : String(error) });
    throw error;
  }

  return data;
};

/**
 * Gets all restaurants across all tenants (for admin operations)
 * @param limit Optional limit on total restaurants returned
 * @returns Array of restaurants with tenant information
 */
const getAllLocationsForAdmin = async (limit?: number): Promise<(DocumentWithId<LocationRecord> & { tenantName: string; tenantDisplayName: string; tenantGuid: string; tenantDocId: string })[]> => {
  logger.debug(`Getting all restaurants for admin view${limit ? ` (limit: ${limit})` : ''}`);

  try {
    // First get all tenants
    const tenantRepo = new FirestoreRepository<Tenant>(CollectionPaths.tenants(), Tenant);
    const tenants = await tenantRepo.find();

    if (tenants.length === 0) {
      logger.debug(`No tenants found`);
      return [];
    }

    const allLocations: (DocumentWithId<LocationRecord> & { tenantName: string; tenantDisplayName: string; tenantGuid: string; tenantDocId: string })[] = [];

    // Get restaurants for each tenant
    for (const { docId: tenantDocId, ...tenantData } of tenants) {
      try {
        const locationCollectionPath = CollectionPaths.restaurants(tenantDocId);
        const locationRepo = new FirestoreRepository<LocationRecord>(locationCollectionPath, LocationRecord);
        const locations = await locationRepo.find();

        // Add tenant information to each restaurant. tenantDocId is stamped so
        // callers (e.g. the device-fleet LocationPicker) can send the fast
        // subcollection scope without re-resolving the tenant from its GUID.
        const locationsWithTenant = locations.map((locationRecord: DocumentWithId<LocationRecord>) => {
          return Object.assign(locationRecord, {
            tenantName: tenantData.name || 'Unknown Tenant',
            tenantDisplayName: tenantData.displayName || tenantData.name || 'Unknown Tenant',
            tenantGuid: tenantData.guid,
            tenantDocId,
          });
        });

        allLocations.push(...locationsWithTenant);

        // Check if we've hit the limit
        if (limit && allLocations.length >= limit) {
          break;
        }
      } catch (error) {
        logger.warn(`Failed to get restaurants for tenant ${tenantDocId}`, { error: error instanceof Error ? error.message : String(error), tenantDocId });
        // Continue with other tenants
      }
    }

    logger.debug(`Found ${allLocations.length} restaurants across ${tenants.length} tenants`);
    return limit ? allLocations.slice(0, limit) : allLocations;

  } catch (error) {
    logger.error(`Failed to get all restaurants for admin`, { error: error instanceof Error ? error.message : String(error) });
    return [];
  }
};

/**
 * Searches restaurants across all tenants by name, concept, or tenant name
 * @param searchTerm The search term
 * @param limit Maximum results to return
 * @returns Array of matching restaurants with tenant information
 */
const searchLocationsForAdmin = async (searchTerm: string, limit: number = 20): Promise<(DocumentWithId<LocationRecord> & { tenantName: string; tenantDisplayName: string })[]> => {
  if (!searchTerm.trim()) return [];

  logger.debug(`Searching restaurants for admin with term: "${searchTerm}"`);

  const allLocations = await getAllLocationsForAdmin();
  const searchLower = searchTerm.toLowerCase();

  const filteredLocations = allLocations.filter(locationRecord =>
    locationRecord.name.toLowerCase().includes(searchLower) ||
    locationRecord.concept.toLowerCase().includes(searchLower) ||
    locationRecord.tenantName.toLowerCase().includes(searchLower) ||
    locationRecord.tenantDisplayName.toLowerCase().includes(searchLower) ||
    locationRecord.address.city.toLowerCase().includes(searchLower) ||
    locationRecord.address.state.toLowerCase().includes(searchLower)
  );

  logger.debug(`Found ${filteredLocations.length} restaurants matching search term`);
  return filteredLocations.slice(0, limit);
};

/**
 * Checks if a restaurant slug is available across all tenants via the auth'd
 * API endpoint `GET /api/v1/locations/slug-availability` (staff-claims
 * gated, Admin-SDK collection-group query server-side).
 *
 * PR-0: this used to run a CLIENT-SDK restaurants collection-group query,
 * which the tightened (site-admin-only) collection-group rule no longer
 * permits for management staff. The server now answers with a bare boolean —
 * `existingRestaurant` is intentionally never populated anymore (the old path
 * handed the full FOREIGN restaurant doc to any staff member; no caller
 * consumed it — the domains page reads only `.available`). The field stays in
 * the return type so the signature is unchanged.
 *
 * Fail-closed contract preserved: any transport/auth/server error reports the
 * slug as NOT available, exactly like the old query-error branch.
 *
 * @param slug The slug to check
 * @param excludeLocationNumericId Optional restaurant ID to exclude from the check (for updates)
 * @returns Promise<{ available: boolean; existingRestaurant?: DocumentWithId<Restaurant> }>
 */
const checkSlugAvailability = async (slug: string, excludeLocationNumericId?: number): Promise<{ available: boolean; existingRestaurant?: DocumentWithId<LocationRecord> }> => {
  logger.debug(`Checking slug availability: ${slug}${excludeLocationNumericId ? ` (excluding restaurantId: ${excludeLocationNumericId})` : ''}`);

  if (!slug || typeof slug !== 'string') {
    logger.warn(`Invalid slug provided: ${slug}`);
    return { available: false };
  }

  // Normalize slug (same as generateSlug function). The server normalizes
  // identically; doing it here too keeps the logged value canonical.
  const normalizedSlug = slug.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
  if (!normalizedSlug) {
    return { available: false };
  }

  try {
    const params = new URLSearchParams({ slug: normalizedSlug });
    if (typeof excludeLocationNumericId === 'number') {
      params.set('excludeRestaurantId', String(excludeLocationNumericId));
    }

    const response = await getApi<{ success: boolean; data?: { available?: boolean } }>(
      locationsApi,
      `/slug-availability?${params.toString()}`,
    );

    const available = response?.success === true && response.data?.available === true;
    logger.debug(`Slug "${normalizedSlug}" is ${available ? 'available' : 'not available'}`);
    return { available };
  } catch (error) {
    logger.error(`Failed to check slug availability for ${normalizedSlug}`, { error: error instanceof Error ? error.message : String(error), slug: normalizedSlug });
    // On error, assume not available to be safe (fail closed)
    return { available: false };
  }
};

export const locationService = {
  getRestaurantsByTenantId: getLocationsByTenantId,
  getRestaurantById: getLocationById,
  getRestaurantByGuid: getLocationByGuid,
  getRestaurantBySlug: getLocationBySlug,
  createRestaurant: createLocation,
  updateRestaurant: updateLocation,
  updateRestaurantByGuid: updateLocationByGuid,
  syncToFirestore,
  fetchFromFirestore,
  // Admin functions
  getAllRestaurantsForAdmin: getAllLocationsForAdmin,
  searchRestaurantsForAdmin: searchLocationsForAdmin,
  // Slug operations
  checkSlugAvailability,
  // Utility functions for document ID resolution
  resolveTenantDocId,
  resolveRestaurantDocId: resolveLocationDocId,
};
